import {
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Grid,
  Input,
  List,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PATHS } from "../../../app/router/paths";
import { isApiError } from "../../../shared/errors/ApiError";
import {
  bulkDeleteLeadTimeTargets,
  bulkUpdateLeadTimeTargets,
  deleteLeadTimeTarget,
  fetchLeadTimeTargets,
  updateLeadTimeTarget,
} from "../api/productDeadlineApi";
import { BulkActionBar } from "../components/BulkActionBar";
import { EditRuleModal } from "../components/EditRuleModal";
import { MobilePagination } from "../../../shared/components/MobilePagination";
import { PageHeader } from "../../../shared/components/PageHeader";
import type {
  LeadTimeConfigurationFormValues,
  LeadTimeRuleTarget,
} from "../model/types";
import { mergePageSelection, removeSelectedIds } from "../model/selection";
import { buildLeadTimeConfiguration } from "../model/configuration";
import { appNotification } from "../../../shared/notifications/appNotification";
import { formatApiTimestamp } from "../../../shared/utils/apiTimestamp";
import "../styles/ProductDeadline.css";
import { usePageRefresh } from "../../../shared/pull-refresh/PullRefreshContext";

const { useBreakpoint } = Grid;
const { Text } = Typography;

const errorMessage = (error: unknown) =>
  isApiError(error) ? error.message : "処理中にエラーが発生しました。";

const formatTimestamp = (value?: string) => formatApiTimestamp(value, "date");

const CustomerCell = ({ target }: { target: LeadTimeRuleTarget }) => (
  <div className="deadline-product-cell">
    <Text strong>{target.customer_code || "商品デフォルト"}</Text>
  </div>
);

export const ProductDeadline = () => {
  const registerPageRefresh = usePageRefresh();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();
  const [items, setItems] = useState<LeadTimeRuleTarget[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [editingTarget, setEditingTarget] = useState<LeadTimeRuleTarget>();
  const [deleteTarget, setDeleteTarget] = useState<LeadTimeRuleTarget>();
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [mobileSelectMode, setMobileSelectMode] = useState(false);

  const loadTargets = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchLeadTimeTargets({
        page,
        page_size: pageSize,
        search: search || undefined,
      });
      setItems(result.items);
      setTotal(result.pagination.total);
      if (
        page > 1 &&
        result.items.length === 0 &&
        result.pagination.total > 0
      ) {
        setPage(Math.max(result.pagination.total_pages, 1));
      }
    } catch (error) {
      appNotification.error({
        message: "注文期限を取得できませんでした",
        description: errorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    void loadTargets();
  }, [loadTargets]);

  useEffect(() => {
    registerPageRefresh({ enabled: true, refresh: loadTargets });
    return () => registerPageRefresh(null);
  }, [registerPageRefresh, loadTargets]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const clearSelection = () => setSelectedIds(new Set());

  const submitSingleEdit = async (values: LeadTimeConfigurationFormValues) => {
    if (!editingTarget) return;
    setSaving(true);
    try {
      await updateLeadTimeTarget(
        editingTarget.lead_time_rule_target_id,
        buildLeadTimeConfiguration(values),
      );
      appNotification.success({
        message: "注文期限を更新しました",
        description: "この顧客と商品の注文期限設定を更新しました。",
      });
      setEditingTarget(undefined);
      await loadTargets();
    } catch (error) {
      appNotification.error({
        message: "更新できませんでした",
        description: errorMessage(error),
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmSingleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      const targetId = deleteTarget.lead_time_rule_target_id;
      await deleteLeadTimeTarget(targetId);
      setSelectedIds((previous) => removeSelectedIds(previous, [targetId]));
      appNotification.success({ message: "注文期限設定を削除しました" });
      setDeleteTarget(undefined);
      await loadTargets();
    } catch (error) {
      appNotification.error({
        message: "削除できませんでした",
        description: errorMessage(error),
      });
    } finally {
      setSaving(false);
    }
  };

  const submitBulkEdit = async (values: LeadTimeConfigurationFormValues) => {
    setSaving(true);
    try {
      const result = await bulkUpdateLeadTimeTargets(
        Array.from(selectedIds),
        buildLeadTimeConfiguration(values),
      );
      appNotification.success({
        message: `${result.updated_count}件のルールを更新しました`,
      });
      setBulkEditOpen(false);
      clearSelection();
      await loadTargets();
    } catch (error) {
      appNotification.error({
        message: "一括更新できませんでした",
        description: errorMessage(error),
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmBulkDelete = async () => {
    setSaving(true);
    try {
      const result = await bulkDeleteLeadTimeTargets(Array.from(selectedIds));
      setSelectedIds((previous) =>
        removeSelectedIds(previous, result.deleted_ids),
      );
      appNotification.success({
        message: `${result.deleted_count}件の設定を削除しました`,
      });
      setBulkDeleteOpen(false);
      await loadTargets();
    } catch (error) {
      appNotification.error({
        message: "一括削除できませんでした",
        description: errorMessage(error),
      });
    } finally {
      setSaving(false);
    }
  };

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: items
        .filter((item) => selectedIds.has(item.lead_time_rule_target_id))
        .map((item) => item.lead_time_rule_target_id),
      preserveSelectedRowKeys: true,
      getCheckboxProps: (record: LeadTimeRuleTarget) => ({
        disabled: record.customer_code === null,
        title:
          record.customer_code === null
            ? "商品デフォルトはこの画面では編集できません"
            : undefined,
      }),
      onChange: (keys: React.Key[]) => {
        setSelectedIds((previous) =>
          mergePageSelection(
            previous,
            items.map((item) => item.lead_time_rule_target_id),
            keys as number[],
          ),
        );
      },
    }),
    [items, selectedIds],
  );

  const columns: ColumnsType<LeadTimeRuleTarget> = useMemo(
    () => [
      {
        title: "顧客コード",
        ellipsis: true,
        minWidth: 110,
        fixed: screens.lg ? "left" : undefined,
        render: (_, record) => <CustomerCell target={record} />,
      },
      {
        title: "商品",
        ellipsis: true,
        minWidth: 230,
        render: (_, record) => (
          <div className="deadline-product-cell">
            <Text strong>{record.product_code}</Text>
            <Text
              type="secondary"
              ellipsis={{ tooltip: record.product_name || undefined }}
            >
              {record.product_name || "商品名なし"}
            </Text>
          </div>
        ),
      },
      {
        title: "必要リードタイム",
        ellipsis: true,
        minWidth: 50,
        render: (_, record) => (
          <Tag color="gold">{record.lead_time_days}日</Tag>
        ),
      },
      {
        title: "締切ルール",
        ellipsis: true,
        minWidth: 120,
        render: (_, record) =>
          record.cutoff_time ? (
            <div className="deadline-cutoff-cell">
              <Text strong>{record.cutoff_time.slice(0, 5)} まで</Text>
              <Text type="secondary">締切後 +{record.cutoff_extra_days}日</Text>
            </div>
          ) : (
            <Text type="secondary">時刻締切なし</Text>
          ),
      },
      {
        title: "メモ",
        ellipsis: true,
        minWidth: 210,
        render: (_, record) => (
          <Text ellipsis={{ tooltip: record.description || undefined }}>
            {record.description || "メモなし"}
          </Text>
        ),
      },
      {
        title: "最終更新",
        ellipsis: true,
        minWidth: 135,
        render: (_, record) => (
          <Text type="secondary">{formatTimestamp(record.updated_at)}</Text>
        ),
      },
      {
        title: "操作",
        ellipsis: true,
        minWidth: 112,
        fixed: screens.lg ? "right" : undefined,
        align: "center",
        render: (_, record) => (
          <Space size={4}>
            <Tooltip
              title={
                record.customer_code
                  ? "この行のルールを変更"
                  : "商品デフォルトはこの画面では編集できません"
              }
            >
              <Button
                type="primary"
                style={{ borderRadius: 50 }}
                icon={<EditOutlined />}
                disabled={!record.customer_code}
                aria-label="この行のルールを変更"
                onClick={(event) => {
                  event.stopPropagation();
                  setEditingTarget(record);
                }}
              />
            </Tooltip>
            <Tooltip title="この設定を削除">
              <Button
                danger
                style={{ borderRadius: 50 }}
                icon={<DeleteOutlined />}
                aria-label="この設定を削除"
                onClick={(event) => {
                  event.stopPropagation();
                  setDeleteTarget(record);
                }}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [screens.lg],
  );

  return (
    <div className="product-deadline-page">
      <PageHeader
        title="注文期限管理"
        description="顧客と商品ごとに、必要日数と締切時刻を管理します。"
        actionLabel="新規設定"
        onAction={() => navigate(PATHS.PRODUCT_DEADLINE.NEW)}
      />

      <Card className="deadline-list-card" bordered={false}>
        <Row
          gutter={[12, 12]}
          justify="space-between"
          align="middle"
          className="deadline-toolbar"
        >
          <Col xs={24} md={15} lg={12}>
            <Input
              allowClear
              size="large"
              prefix={<SearchOutlined />}
              value={searchInput}
              placeholder={
                isMobile
                  ? "顧客・商品・メモを検索"
                  : "顧客コード・顧客名・商品コード・商品名・メモで検索"
              }
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </Col>
          <Col xs={24} md="auto">
            <Space className="deadline-toolbar-actions">
              <div className="deadline-total">
                <TeamOutlined /> <strong>{total}</strong> 件の設定
              </div>
              {!isMobile && (
                <Button
                  size="large"
                  icon={<ReloadOutlined />}
                  loading={loading}
                  onClick={() => void loadTargets()}
                >
                  更新
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        <BulkActionBar
          count={selectedIds.size}
          loading={saving}
          onEdit={() => setBulkEditOpen(true)}
          onDelete={() => setBulkDeleteOpen(true)}
          onClear={clearSelection}
        />

        {isMobile ? (
          <div className="deadline-mobile-list">
            <div className="deadline-mobile-select-toolbar">
              <button
                type="button"
                className={`deadline-mobile-select-toggle${mobileSelectMode ? " deadline-mobile-select-toggle--active" : ""}`}
                onClick={() => {
                  setMobileSelectMode((value) => !value);
                  if (mobileSelectMode) clearSelection();
                }}
              >
                {mobileSelectMode ? "選択モード終了" : "複数選択"}
              </button>
            </div>
            <List
              loading={loading}
              dataSource={items}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      search
                        ? "検索条件に一致する設定がありません"
                        : "注文期限の設定がありません"
                    }
                  >
                    {!search && (
                      <Button
                        type="primary"
                        onClick={() => navigate(PATHS.PRODUCT_DEADLINE.NEW)}
                      >
                        最初の設定を追加
                      </Button>
                    )}
                  </Empty>
                ),
              }}
              renderItem={(record) => {
                const selected = selectedIds.has(
                  record.lead_time_rule_target_id,
                );
                const selectable = record.customer_code !== null;
                const toggle = () => {
                  if (!selectable) return;
                  setSelectedIds((previous) => {
                    const next = new Set(previous);
                    if (next.has(record.lead_time_rule_target_id))
                      next.delete(record.lead_time_rule_target_id);
                    else next.add(record.lead_time_rule_target_id);
                    return next;
                  });
                };
                return (
                  <List.Item className="deadline-mobile-list-item">
                    <article
                      className={`deadline-mobile-rule-card${selected ? " deadline-mobile-rule-card--selected" : ""}`}
                      onClick={() =>
                        mobileSelectMode
                          ? toggle()
                          : selectable && setEditingTarget(record)
                      }
                    >
                      <div className="deadline-mobile-card-header">
                        {mobileSelectMode && selectable && (
                          <Checkbox
                            checked={selected}
                            onChange={toggle}
                            onClick={(event) => event.stopPropagation()}
                          />
                        )}
                        <Text
                          type="secondary"
                          strong
                          style={{ color: "#000", fontSize: 18 }}
                        >
                          顧客 {record.customer_code || "商品デフォルト"}
                        </Text>
                        <Text
                          type="secondary"
                          className="deadline-mobile-updated"
                        >
                          {formatTimestamp(record.updated_at)}
                        </Text>
                      </div>
                      <div className="deadline-mobile-product">
                        <Text strong>{record.product_code}</Text>
                        <Text type="secondary">
                          {record.product_name || "商品名なし"}
                        </Text>
                      </div>
                      <div className="deadline-mobile-rule-grid">
                        <div className="deadline-mobile-rule-value">
                          <Text type="secondary">必要リードタイム</Text>
                          <Tag color="gold">{record.lead_time_days}日</Tag>
                        </div>
                        <div className="deadline-mobile-rule-value">
                          <Text type="secondary">締切</Text>
                          <Text strong>
                            {record.cutoff_time
                              ? `${record.cutoff_time.slice(0, 5)}まで・後+${record.cutoff_extra_days}日`
                              : "時刻締切なし"}
                          </Text>
                        </div>
                      </div>
                      {record.description && (
                        <div className="deadline-mobile-rule-name">
                          <Text type="secondary" ellipsis>
                            {record.description}
                          </Text>
                        </div>
                      )}
                      <Space
                        direction="vertical"
                        style={{ width: "100%" }}
                        size={8}
                      >
                        <Button
                          block
                          type="primary"
                          icon={<EditOutlined />}
                          disabled={!selectable}
                          onClick={(event) => {
                            event.stopPropagation();
                            setEditingTarget(record);
                          }}
                        >
                          この設定を変更
                        </Button>
                        <Button
                          block
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(event) => {
                            event.stopPropagation();
                            setDeleteTarget(record);
                          }}
                        >
                          この設定を削除
                        </Button>
                      </Space>
                    </article>
                  </List.Item>
                );
              }}
            />
            <MobilePagination
              current={page}
              pageSize={pageSize}
              total={total}
              onChange={setPage}
            />
          </div>
        ) : (
          <Table<LeadTimeRuleTarget>
            rowKey="lead_time_rule_target_id"
            columns={columns}
            dataSource={items}
            loading={loading}
            scroll={{ x: 800 }}
            rowSelection={rowSelection}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    search
                      ? "検索条件に一致する設定がありません"
                      : "注文期限の設定がありません"
                  }
                >
                  {!search && (
                    <Button
                      type="primary"
                      onClick={() => navigate(PATHS.PRODUCT_DEADLINE.NEW)}
                    >
                      最初の設定を追加
                    </Button>
                  )}
                </Empty>
              ),
            }}
            pagination={{
              current: page,
              pageSize,
              total,
              size: "middle",
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (value, range) =>
                `${value} 件中 ${range[0]}-${range[1]} を表示`,
              onChange: (nextPage, nextSize) => {
                setPage(nextSize !== pageSize ? 1 : nextPage);
                setPageSize(nextSize);
              },
            }}
            onRow={(record) => ({
              className: record.customer_code ? "deadline-clickable-row" : "",
              onClick: (event) => {
                if (
                  !record.customer_code ||
                  (event.target as HTMLElement).closest(".ant-checkbox-wrapper")
                )
                  return;
                setEditingTarget(record);
              },
            })}
          />
        )}
      </Card>

      <EditRuleModal
        open={Boolean(editingTarget)}
        loading={saving}
        target={editingTarget}
        onCancel={() => setEditingTarget(undefined)}
        onSubmit={submitSingleEdit}
      />
      <EditRuleModal
        open={bulkEditOpen}
        loading={saving}
        bulkCount={selectedIds.size}
        onCancel={() => setBulkEditOpen(false)}
        onSubmit={submitBulkEdit}
      />

      <Modal
        open={bulkDeleteOpen}
        centered
        title={`${selectedIds.size}件の設定を削除しますか？`}
        okText="削除する"
        cancelText="キャンセル"
        okButtonProps={{ danger: true, loading: saving }}
        onCancel={() => setBulkDeleteOpen(false)}
        onOk={() => void confirmBulkDelete()}
      >
        <p style={{ margin: 0, color: "#595959" }}>
          選択した <strong>{selectedIds.size}</strong>{" "}
          件の注文期限設定を削除します。削除後は元に戻せません。
        </p>
      </Modal>
      <Modal
        open={Boolean(deleteTarget)}
        centered
        title="設定を削除しますか？"
        okText="削除する"
        cancelText="キャンセル"
        okButtonProps={{ danger: true, loading: saving }}
        onCancel={() => setDeleteTarget(undefined)}
        onOk={() => void confirmSingleDelete()}
      >
        {deleteTarget && (
          <div className="deadline-delete-confirm-body">
            <p className="deadline-delete-confirm-lead">
              以下の注文期限設定を削除します。削除後は元に戻せません。
            </p>
            <div className="deadline-delete-confirm-target">
              <span className="deadline-delete-confirm-label">顧客</span>
              <strong>{deleteTarget.customer_code || "商品デフォルト"}</strong>
            </div>
            <div className="deadline-delete-confirm-target">
              <span className="deadline-delete-confirm-label">商品</span>
              <strong>{deleteTarget.product_code}</strong>
              <span className="deadline-delete-confirm-sub">
                {deleteTarget.product_name}
              </span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
